import { readyAudioStatusSet } from '@listening/api-contract/audio-statuses';
import {
  NotFoundError,
  UnauthorizedError,
} from '@listening/api-contract/errors';
import type { AudioItemDetail } from '@listening/api-contract/legacy-client-types';
import { Schema } from 'effect';
import { type QueryClient, useQuery } from '@tanstack/react-query';
import {
  AUDIO_DETAIL_STALE_TIME_MS,
  AudioItems,
  audioItemDetailQueryKey,
  audioItemSummaryQueryKey,
  audioPageImageQueryKey,
} from './audio-items';
import { unwrapEffectQueryFailure } from './query-error';
import { useEq } from './runtime-context';

// Per-id audio reads, shared across apps (start / mobile / browser-ext). The
// effects + keys live in `audio-items.ts`; this file exposes the eq-agnostic
// query args (route loaders wrap with `getEq().queryOptions(...)`) plus the
// shared reader hook + a sync cache read. The detail cache is patched in place
// by the optimistic walk (walk-audio-caches.ts), so an edit re-renders the
// player directly — no merge-on-read against a list cache.

// `/v2/audio/{audio_or_slug}/detailed` → `AudioConversionDetail` (Summary +
// chapters + voice).
export const audioDetailArgs = (audioId: string | undefined) => ({
  queryKey: audioItemDetailQueryKey(audioId ?? ''),
  queryFn: () => AudioItems.detailedEffect(audioId ?? ''),
  enabled: !!audioId,
  staleTime: AUDIO_DETAIL_STALE_TIME_MS,
});

// Single-audio Summary fetch by id — distinct cache from the detailed fetch
// (hits `/v2/audio/:id`, no chapters). Used by the doc/$docId SSR loader for the
// metadata-fallback path when the detail endpoint fails.
export const audioSummaryArgs = (audioId: string | undefined) => ({
  queryKey: audioItemSummaryQueryKey(audioId ?? ''),
  queryFn: () => AudioItems.detailEffect(audioId ?? ''),
  enabled: !!audioId,
  staleTime: AUDIO_DETAIL_STALE_TIME_MS,
});

export const AUDIO_PAGE_IMAGE_CACHE_GC_TIME_MS = 120_000;

export const audioPageImageArgs = (
  audioId: string | undefined,
  page: number,
  enabled = true,
) => ({
  queryKey: audioPageImageQueryKey(audioId ?? '', page),
  queryFn: () => AudioItems.pageImageEffect(audioId ?? '', page),
  enabled: !!audioId && page >= 1 && enabled,
  staleTime: Number.POSITIVE_INFINITY,
  gcTime: AUDIO_PAGE_IMAGE_CACHE_GC_TIME_MS,
});

// Reactive detailed-audio view (`AudioConversionDetail`). Optimistic edits land
// on this cache via the walk, so the player re-renders without merging from any
// list cache. On the public `/doc/$docId` SSR route the detail payload carries
// the Summary fields itself.
export const useAudioDetailedView = (audioId: string | undefined) =>
  useQuery(useEq().queryOptions(audioDetailArgs(audioId)));

// Synchronous cache read for non-hook callsites (e.g. closures held in a zustand
// store ref). Reads the raw detail cache.
export const getAudioDetailFromCache = (
  queryClient: QueryClient,
  audioId: string | undefined,
) => {
  if (!audioId) return undefined;
  return queryClient.getQueryData(audioItemDetailQueryKey(audioId));
};

const isNotFound = Schema.is(NotFoundError);
const isUnauthorized = Schema.is(UnauthorizedError);

/** The audio detail fetch failed. Never fatal on its own — the caller decides
 * what a still-missing detail means. */
export class AudioDetailFetchError extends Schema.TaggedError<AudioDetailFetchError>()(
  'AudioDetailFetchError',
  {
    audioId: Schema.String,
    cause: Schema.Unknown,
  },
) {
  /** The server answered that this viewer cannot have the document — it does
   * not exist, was deleted, or is someone else's private document — so
   * retrying cannot help. Only an explicit `access-denied` 401 counts: a
   * refusal for want of a session (an expired sign-in) or one that gives no
   * reason stays retryable, since signing in again may open it. */
  get documentUnavailable() {
    const failure = unwrapEffectQueryFailure(this.cause);
    return (
      isNotFound(failure) ||
      (isUnauthorized(failure) && failure.reason === 'access-denied')
    );
  }
}

/** The conversion has produced enough for playback to start. */
export const isAudioDetailReadyForStreaming = (
  audioItemDetail: Pick<AudioItemDetail, 'conversion_status'>,
) => readyAudioStatusSet.has(audioItemDetail.conversion_status);
